import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CircularProgress from '@mui/material/CircularProgress';
import type { ImageAttachment } from '../../../types/imageAttachment';
import './ImageAttachmentsGrid.css';

type LoadStatus = 'loading' | 'loaded' | 'error';

type LoadState = {
  status: LoadStatus;
  previewUrl: string;
};

type RefreshReason = 'preload' | 'error';

type Props = {
  images: ImageAttachment[];
  onRemoveImage: (id: string) => void;
  editable?: boolean;
  lazy?: boolean;
  progressiveLoad?: boolean;
  loadingPlaceholderCount?: number;
  disableActions?: boolean;
  onRefreshImageUrl?: (image: ImageAttachment) => Promise<ImageAttachment | null | undefined>;
};

const PRESIGNED_URL_REFRESH_MARGIN_MS = 30_000;
const PRELOAD_REFRESH_MIN_INTERVAL_MS = 30_000;
const IMAGE_LOAD_CONCURRENCY = 30;
const IMAGE_LAZY_ROOT_MARGIN = '900px 0px';

let imageGridInstanceSeq = 0;
let activeImageLoadCount = 0;

const queuedImageLoadTasks: Array<{ key: string; start: () => void }> = [];
const queuedImageLoadKeys = new Set<string>();
const activeImageLoadKeys = new Set<string>();

const pumpImageLoadQueue = () => {
  while (activeImageLoadCount < IMAGE_LOAD_CONCURRENCY && queuedImageLoadTasks.length > 0) {
    const task = queuedImageLoadTasks.shift();
    if (!task) return;
    queuedImageLoadKeys.delete(task.key);
    if (activeImageLoadKeys.has(task.key)) continue;

    activeImageLoadKeys.add(task.key);
    activeImageLoadCount += 1;
    task.start();
  }
};

const removeQueuedImageLoadTask = (key: string) => {
  const index = queuedImageLoadTasks.findIndex((task) => task.key === key);
  if (index >= 0) queuedImageLoadTasks.splice(index, 1);
  queuedImageLoadKeys.delete(key);
};

const requestImageLoadSlot = (key: string, start: () => void) => {
  if (activeImageLoadKeys.has(key) || queuedImageLoadKeys.has(key)) return;

  if (activeImageLoadCount < IMAGE_LOAD_CONCURRENCY) {
    activeImageLoadKeys.add(key);
    activeImageLoadCount += 1;
    start();
    return;
  }

  queuedImageLoadKeys.add(key);
  queuedImageLoadTasks.push({ key, start });
};

const releaseImageLoadSlot = (key: string) => {
  if (queuedImageLoadKeys.has(key)) {
    removeQueuedImageLoadTask(key);
    return;
  }

  if (!activeImageLoadKeys.delete(key)) return;
  activeImageLoadCount = Math.max(0, activeImageLoadCount - 1);
  pumpImageLoadQueue();
};

const needsPresignedUrlRefresh = (image: ImageAttachment): boolean => {
  const expiresAtMs = Number(image.previewUrlExpiresAtMs ?? 0);
  if (!image.fileKey || !Number.isFinite(expiresAtMs) || expiresAtMs <= 0) return false;
  return expiresAtMs - Date.now() <= PRESIGNED_URL_REFRESH_MARGIN_MS;
};

const createImageGridInstanceId = () => {
  imageGridInstanceSeq += 1;
  return `image-grid-${imageGridInstanceSeq}`;
};

const makeImageLoadTaskKey = (gridId: string, image: ImageAttachment) =>
  `${gridId}:${image.id}:${image.previewUrl}`;

const ImageAttachmentsGrid = memo((props: Props) => {
  const {
    images,
    onRemoveImage,
    editable = false,
    lazy = false,
    progressiveLoad = false,
    loadingPlaceholderCount = 0,
    disableActions = false,
    onRefreshImageUrl,
  } = props;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [loadStateById, setLoadStateById] = useState<Record<string, LoadState>>({});
  const [loadUrlById, setLoadUrlById] = useState<Record<string, string>>({});
  const [refreshingById, setRefreshingById] = useState<Record<string, boolean>>({});
  const [isInView, setIsInView] = useState(!lazy);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const gridInstanceIdRef = useRef<string | null>(null);
  const loadTaskByIdRef = useRef<Record<string, { key: string; previewUrl: string }>>({});
  const refreshInFlightByIdRef = useRef<
    Record<string, Promise<ImageAttachment | null | undefined> | undefined>
  >({});
  const errorRetryAttemptedByIdRef = useRef<Record<string, boolean>>({});
  const lastPreloadRefreshAtByIdRef = useRef<Record<string, number>>({});

  if (gridInstanceIdRef.current == null) {
    gridInstanceIdRef.current = createImageGridInstanceId();
  }

  useEffect(() => {
    if (!lazy || progressiveLoad) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const node = gridRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: IMAGE_LAZY_ROOT_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazy, progressiveLoad]);

  const releaseImageLoadForId = useCallback((id: string, previewUrl?: string) => {
    const task = loadTaskByIdRef.current[id];
    if (!task) return;
    if (previewUrl && task.previewUrl !== previewUrl) return;

    releaseImageLoadSlot(task.key);
    delete loadTaskByIdRef.current[id];
  }, []);

  useEffect(() => {
    const imageIds = new Set(images.map((image) => image.id));
    const previewUrlById = new Map(images.map((image) => [image.id, image.previewUrl]));

    Object.entries(loadTaskByIdRef.current).forEach(([id, task]) => {
      if (previewUrlById.get(id) !== task.previewUrl) {
        releaseImageLoadSlot(task.key);
        delete loadTaskByIdRef.current[id];
      }
    });
    Object.keys(errorRetryAttemptedByIdRef.current).forEach((id) => {
      if (!imageIds.has(id)) delete errorRetryAttemptedByIdRef.current[id];
    });
    Object.keys(lastPreloadRefreshAtByIdRef.current).forEach((id) => {
      if (!imageIds.has(id)) delete lastPreloadRefreshAtByIdRef.current[id];
    });

    setLoadStateById((prev) => {
      const next: Record<string, LoadState> = {};
      images.forEach((image) => {
        const previous = prev[image.id];
        next[image.id] =
          previous && previous.previewUrl === image.previewUrl
            ? previous
            : { status: 'loading', previewUrl: image.previewUrl };
      });
      return next;
    });

    setLoadUrlById((prev) => {
      const next: Record<string, string> = {};
      images.forEach((image) => {
        if (prev[image.id] === image.previewUrl) {
          next[image.id] = prev[image.id];
        }
      });
      return next;
    });
  }, [images]);

  useEffect(() => {
    return () => {
      Object.values(loadTaskByIdRef.current).forEach((task) => {
        releaseImageLoadSlot(task.key);
      });
      loadTaskByIdRef.current = {};
    };
  }, []);

  const shouldQueueLoad = progressiveLoad || !lazy || isInView;

  const queueImageLoad = useCallback((image: ImageAttachment) => {
    const existingTask = loadTaskByIdRef.current[image.id];
    if (existingTask?.previewUrl === image.previewUrl) return;
    if (existingTask) releaseImageLoadSlot(existingTask.key);

    const gridInstanceId = gridInstanceIdRef.current ?? createImageGridInstanceId();
    gridInstanceIdRef.current = gridInstanceId;
    const taskKey = makeImageLoadTaskKey(gridInstanceId, image);
    loadTaskByIdRef.current[image.id] = {
      key: taskKey,
      previewUrl: image.previewUrl,
    };

    requestImageLoadSlot(taskKey, () => {
      setLoadUrlById((prev) => {
        const currentTask = loadTaskByIdRef.current[image.id];
        if (!currentTask || currentTask.key !== taskKey) return prev;
        if (prev[image.id] === image.previewUrl) return prev;
        return { ...prev, [image.id]: image.previewUrl };
      });
    });
  }, []);

  const refreshImage = useCallback(
    async (image: ImageAttachment, reason: RefreshReason) => {
      if (!onRefreshImageUrl || !image.fileKey) return null;

      const inFlight = refreshInFlightByIdRef.current[image.id];
      if (inFlight) return inFlight;

      setRefreshingById((prev) => ({ ...prev, [image.id]: true }));
      setLoadStateById((prev) => ({
        ...prev,
        [image.id]: { status: 'loading', previewUrl: image.previewUrl },
      }));

      const promise = onRefreshImageUrl(image).catch((e) => {
        console.error('Failed to refresh image URL', e);
        return null;
      });
      refreshInFlightByIdRef.current[image.id] = promise;

      try {
        const refreshed = await promise;
        if (refreshed?.previewUrl) {
          setLoadStateById((prev) => ({
            ...prev,
            [image.id]: { status: 'loading', previewUrl: refreshed.previewUrl },
          }));
        } else if (reason === 'error') {
          setLoadStateById((prev) => ({
            ...prev,
            [image.id]: { status: 'error', previewUrl: image.previewUrl },
          }));
        }
        return refreshed;
      } finally {
        delete refreshInFlightByIdRef.current[image.id];
        setRefreshingById((prev) => {
          const next = { ...prev };
          delete next[image.id];
          return next;
        });
      }
    },
    [onRefreshImageUrl]
  );

  useEffect(() => {
    if (!shouldQueueLoad || !onRefreshImageUrl) return;

    const now = Date.now();
    images.forEach((image) => {
      if (!needsPresignedUrlRefresh(image)) return;

      const lastRefreshAt = lastPreloadRefreshAtByIdRef.current[image.id] ?? 0;
      if (now - lastRefreshAt < PRELOAD_REFRESH_MIN_INTERVAL_MS) return;

      lastPreloadRefreshAtByIdRef.current[image.id] = now;
      void refreshImage(image, 'preload');
    });
  }, [images, onRefreshImageUrl, refreshImage, shouldQueueLoad]);

  useEffect(() => {
    if (!shouldQueueLoad) return;

    images.forEach((image) => {
      const loadState = loadStateById[image.id];
      const status =
        loadState && loadState.previewUrl === image.previewUrl ? loadState.status : 'loading';
      if (status === 'loaded' || status === 'error') return;
      if (refreshingById[image.id]) return;
      if (loadUrlById[image.id] === image.previewUrl) return;
      if (onRefreshImageUrl && needsPresignedUrlRefresh(image)) return;

      queueImageLoad(image);
    });
  }, [
    images,
    loadStateById,
    loadUrlById,
    onRefreshImageUrl,
    queueImageLoad,
    refreshingById,
    shouldQueueLoad,
  ]);

  const lightboxImages = useMemo(
    () =>
      images.map((image) => ({
        url: image.previewUrl,
        title: image.name,
      })),
    [images]
  );
  const hasMultipleImages = lightboxImages.length > 1;
  const placeholderCount = Math.max(0, Math.floor(Number(loadingPlaceholderCount) || 0));

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  return (
    <div className="image-attachments-grid" ref={gridRef}>
      {Array.from({ length: placeholderCount }, (_, index) => (
        <div
          key={`loading-placeholder-${index}`}
          className="image-attachments-item image-attachments-placeholder is-loading"
          aria-busy="true"
          aria-label="画像を読み込み中"
        >
          <div className="image-attachments-loading-overlay">
            <CircularProgress size={28} />
            <span>読み込み中</span>
          </div>
        </div>
      ))}
      {images.map((image, index) => {
        const loadState = loadStateById[image.id];
        const status =
          loadState && loadState.previewUrl === image.previewUrl ? loadState.status : 'loading';
        const isLoaded = status === 'loaded';
        const isError = status === 'error';
        const isRefreshing = !!refreshingById[image.id];
        const loadUrl = loadUrlById[image.id];
        const canAttachSrc = loadUrl === image.previewUrl && !isRefreshing;
        const isInteractionDisabled = disableActions || !isLoaded;

        return (
          <div
            key={image.id}
            className={`image-attachments-item${isLoaded ? '' : ' is-loading'}${
              isError ? ' is-error' : ''
            }`}
            role="button"
            tabIndex={isInteractionDisabled ? -1 : 0}
            aria-disabled={isInteractionDisabled}
            onClick={() => {
              if (!isInteractionDisabled) openLightbox(index);
            }}
            onKeyDown={(e) => {
              if (isInteractionDisabled) return;
              if (e.key === 'Enter' || e.key === ' ') openLightbox(index);
            }}
          >
            <img
              className="image-attachments-thumb"
              src={canAttachSrc ? image.previewUrl : undefined}
              alt={image.name}
              loading={lazy && !progressiveLoad ? 'lazy' : undefined}
              onLoad={() => {
                releaseImageLoadForId(image.id, image.previewUrl);
                delete errorRetryAttemptedByIdRef.current[image.id];
                setLoadStateById((prev) => ({
                  ...prev,
                  [image.id]: { status: 'loaded', previewUrl: image.previewUrl },
                }));
              }}
              onError={() => {
                releaseImageLoadForId(image.id, image.previewUrl);
                if (
                  onRefreshImageUrl &&
                  image.fileKey &&
                  !errorRetryAttemptedByIdRef.current[image.id]
                ) {
                  errorRetryAttemptedByIdRef.current[image.id] = true;
                  void refreshImage(image, 'error');
                  return;
                }

                setLoadStateById((prev) => ({
                  ...prev,
                  [image.id]: { status: 'error', previewUrl: image.previewUrl },
                }));
              }}
            />
            {isLoaded ? (
              <div className="image-attachments-hover-overlay">
                <ZoomInIcon className="image-attachments-hover-icon" />
              </div>
            ) : isError ? (
              <div className="image-attachments-error-overlay">
                <span>Load failed</span>
              </div>
            ) : (
              <div className="image-attachments-loading-overlay">
                <CircularProgress size={28} />
                <span>読み込み中</span>
              </div>
            )}
            {editable && (
              <div className="image-attachments-item-actions">
                <Tooltip title="削除" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        if (disableActions) return;
                        e.stopPropagation();
                        onRemoveImage(image.id);
                      }}
                      disabled={disableActions}
                      sx={{ p: 0.25 }}
                      style={{ marginTop: 0 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        );
      })}
      {lightboxIndex != null && lightboxImages.length > 0 && (
        hasMultipleImages ? (
          <Lightbox images={lightboxImages} startIndex={lightboxIndex} onClose={closeLightbox} />
        ) : (
          <Lightbox
            image={lightboxImages[0].url}
            title={lightboxImages[0].title}
            onClose={closeLightbox}
          />
        )
      )}
    </div>
  );
});

ImageAttachmentsGrid.displayName = 'ImageAttachmentsGrid';

export default ImageAttachmentsGrid;
